import { version } from "@environment/version";
import { useRecoilStateLoadable } from "recoil";
import { updateOnboardingAtom, UpdateState } from "@state/UpdateState";
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useHelpCentre } from "./useHelpCentre";

type UpdateOnboarding = {
  updateState: UpdateState | null;
  version: string;
  dismissed: boolean;
  dismissOnboarding: () => void;
};

export const useUpdateOnboarding = (): UpdateOnboarding => {
  const { whatsNewSection } = useHelpCentre();

  const [updateState, setUpdateState] = useRecoilStateLoadable<UpdateState | null>(updateOnboardingAtom);

  useEffect(() => {
    const checkIfUpdated = async (): Promise<void> => {
      if (Capacitor.getPlatform() === "web") {
        if (updateState.contents !== null) {
          const isHigher = isHigherVersion(whatsNewSection!["App Version"]!, updateState.contents.version);
          const isEnabled = whatsNewSection?.Enabled;

          isHigher && isEnabled && setUpdateState({ dismissed: false, version: whatsNewSection!["App Version"]! });
        } else {
          const isEnabled = whatsNewSection?.Enabled;
          isEnabled && setUpdateState({ dismissed: false, version: whatsNewSection!["App Version"]! });
        }
      } else {
        if (updateState.contents !== null) {
          const isHigher = isHigherVersion(whatsNewSection!["App Version"]!, updateState.contents.version);
          const isEnabled = whatsNewSection?.Enabled;

          isHigher && isEnabled && setUpdateState({ dismissed: false, version: whatsNewSection!["App Version"]! });
        } else {
          const isEnabled = whatsNewSection?.Enabled;
          isEnabled && setUpdateState({ dismissed: false, version: whatsNewSection!["App Version"]! });
        }
      }
    };

    if (updateState.state === "hasValue" && whatsNewSection?.["App Version"]) {
      checkIfUpdated();
    }
  }, [updateState.state, whatsNewSection?.["App Version"]]);

  const isHigherVersion = (currentVersion: string, previousVersion: string) => {
    const currentParts = currentVersion.split(".").map(Number);
    const previousParts = previousVersion.split(".").map(Number);

    const maxLength = Math.max(currentParts.length, previousParts.length);

    // Pad the shorter array with zeros to make them the same length
    while (currentParts.length < maxLength) {
      currentParts.push(0);
    }
    while (previousParts.length < maxLength) {
      previousParts.push(0);
    }

    for (let i = 0; i < maxLength; i++) {
      if (currentParts[i] > previousParts[i]) {
        return true;
      } else if (currentParts[i] < previousParts[i]) {
        return false;
      }
    }

    return false; // If versions are equal
  };

  const dismissOnboarding = (): void => {
    setUpdateState({ dismissed: true, version: version });
  };

  return {
    updateState: updateState.state === "hasValue" ? updateState.contents : null,
    dismissed: (updateState.state === "hasValue" && updateState.contents?.dismissed) ?? false,
    version: version,
    dismissOnboarding,
  };
};
